import { ApiResponse, TicketManagerStatusTransition, TicketManagerTicketStatus } from '@vision/ui/interfaces';
import { createHttpExtraOptions } from '../../../utils';
import { TicketManagerApi } from '../ticket-manager.api';
import {
  TicketManagerMutationSuccessResponse,
  TicketManagerProjectTicketStatusesArgs,
  TicketManagerStatusTransitionCreateArgs,
  TicketManagerStatusTransitionDeleteArgs,
  TicketManagerStatusTransitionUpdateArgs,
  TicketManagerStatusTransitionsArgs,
  TicketManagerStatusTransitionsResetArgs,
  TicketManagerTicketAvailableStatusesArgs,
  TicketManagerTicketStatusCreateArgs,
  TicketManagerTicketStatusDeleteArgs,
  TicketManagerTicketStatusUpdateArgs,
} from './ticket-statuses.api.types';
import {
  normalizeStatusTransitionsResponse,
  normalizeTicketManagerTicketStatusesResponse,
  normalizeTicketManagerTicketStatusResponse,
} from './ticket-statuses.mappers';
import {
  buildCreateStatusTransitionRequest,
  buildCreateTicketStatusRequest,
  buildDeleteStatusTransitionRequest,
  buildDeleteTicketStatusRequest,
  buildGetProjectTicketStatusesRequest,
  buildGetStatusTransitionsRequest,
  buildGetTicketAvailableStatusesRequest,
  buildResetStatusTransitionsRequest,
  buildUpdateStatusTransitionRequest,
  buildUpdateTicketStatusRequest,
} from './ticket-statuses.routes';

export const TicketStatusEndpoints = TicketManagerApi.injectEndpoints({
  endpoints: (builder) => ({
    ticketManagerTicketStatuses: builder.query<
      ApiResponse<TicketManagerTicketStatus[]>,
      TicketManagerProjectTicketStatusesArgs
    >({
      query: ({ projectId, query = '' }) => buildGetProjectTicketStatusesRequest({ projectId, query }),
      transformResponse: normalizeTicketManagerTicketStatusesResponse,
      providesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerTicketStatuses', id: projectId }],
    }),

    ticketManagerStatusTransitions: builder.query<
      ApiResponse<TicketManagerStatusTransition[]>,
      TicketManagerStatusTransitionsArgs
    >({
      query: ({ projectId, query = '' }) => buildGetStatusTransitionsRequest({ projectId, query }),
      transformResponse: normalizeStatusTransitionsResponse,
      providesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerStatusTransitions', id: projectId }],
    }),

    ticketManagerTicketAvailableStatuses: builder.query<
      ApiResponse<TicketManagerTicketStatus[]>,
      TicketManagerTicketAvailableStatusesArgs
    >({
      query: ({ projectId, ticketId }) => buildGetTicketAvailableStatusesRequest({ projectId, ticketId }),
      transformResponse: normalizeTicketManagerTicketStatusesResponse,
      providesTags: (_result, _error, { ticketId }) => [{ type: 'TicketManagerTicketAvailableStatuses', id: ticketId }],
    }),

    createTicketManagerTicketStatus: builder.mutation<
      ApiResponse<TicketManagerTicketStatus>,
      TicketManagerTicketStatusCreateArgs
    >({
      query: ({ name, projectId }) => ({
        ...buildCreateTicketStatusRequest({ name, projectId }),
        extraOptions: createHttpExtraOptions({
          notification: {
            translationMessageKey: 'http.message.ticketManagerStatusDefinition.create.{{status}}',
          },
        }),
      }),
      transformResponse: normalizeTicketManagerTicketStatusResponse,
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'TicketManagerTicketStatuses', id: projectId },
        { type: 'TicketManagerStatusTransitions', id: projectId },
        { type: 'TicketManagerTicketAvailableStatuses' },
        { type: 'TicketManagerPoolTickets' },
        { type: 'TicketManagerQueueTickets' },
        { type: 'TicketManagerTicketDetail' },
      ],
    }),

    updateTicketManagerTicketStatus: builder.mutation<
      ApiResponse<TicketManagerTicketStatus>,
      TicketManagerTicketStatusUpdateArgs
    >({
      query: ({ name, projectId, statusId }) => ({
        ...buildUpdateTicketStatusRequest({ name, projectId, statusId }),
        extraOptions: createHttpExtraOptions({
          notification: {
            translationMessageKey: 'http.message.ticketManagerStatusDefinition.update.{{status}}',
          },
        }),
      }),
      transformResponse: normalizeTicketManagerTicketStatusResponse,
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'TicketManagerTicketStatuses', id: projectId },
        { type: 'TicketManagerStatusTransitions', id: projectId },
        { type: 'TicketManagerTicketAvailableStatuses' },
        { type: 'TicketManagerPoolTickets' },
        { type: 'TicketManagerQueueTickets' },
        { type: 'TicketManagerTicketDetail' },
      ],
    }),

    deleteTicketManagerTicketStatus: builder.mutation<
      TicketManagerMutationSuccessResponse,
      TicketManagerTicketStatusDeleteArgs
    >({
      query: ({ projectId, statusId }) => ({
        ...buildDeleteTicketStatusRequest({ projectId, statusId }),
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleError: false,
            translationMessageKey: 'http.message.ticketManagerStatusDefinition.delete.{{status}}',
          },
        }),
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'TicketManagerTicketStatuses', id: projectId },
        { type: 'TicketManagerStatusTransitions', id: projectId },
        { type: 'TicketManagerTicketAvailableStatuses' },
        { type: 'TicketManagerPoolTickets' },
        { type: 'TicketManagerQueueTickets' },
        { type: 'TicketManagerTicketDetail' },
      ],
    }),

    createTicketManagerStatusTransition: builder.mutation<
      ApiResponse<TicketManagerStatusTransition>,
      TicketManagerStatusTransitionCreateArgs
    >({
      query: ({ fromStatusId, projectId, toStatusId }) =>
        buildCreateStatusTransitionRequest({ fromStatusId, projectId, toStatusId }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerStatusTransitions', id: projectId }],
    }),

    updateTicketManagerStatusTransition: builder.mutation<
      ApiResponse<TicketManagerStatusTransition>,
      TicketManagerStatusTransitionUpdateArgs
    >({
      query: ({ fromStatusId, projectId, toStatusId, transitionId }) =>
        buildUpdateStatusTransitionRequest({ fromStatusId, projectId, toStatusId, transitionId }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerStatusTransitions', id: projectId }],
    }),

    deleteTicketManagerStatusTransition: builder.mutation<
      TicketManagerMutationSuccessResponse,
      TicketManagerStatusTransitionDeleteArgs
    >({
      query: ({ projectId, transitionId }) => buildDeleteStatusTransitionRequest({ projectId, transitionId }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerStatusTransitions', id: projectId }],
    }),

    resetTicketManagerStatusTransitions: builder.mutation<
      TicketManagerMutationSuccessResponse,
      TicketManagerStatusTransitionsResetArgs
    >({
      query: ({ projectId }) => buildResetStatusTransitionsRequest({ projectId }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerStatusTransitions', id: projectId }],
    }),
  }),
});

export const {
  useTicketManagerTicketStatusesQuery,
  useTicketManagerStatusTransitionsQuery,
  useTicketManagerTicketAvailableStatusesQuery,
  useLazyTicketManagerTicketAvailableStatusesQuery,
  useCreateTicketManagerTicketStatusMutation,
  useUpdateTicketManagerTicketStatusMutation,
  useDeleteTicketManagerTicketStatusMutation,
  useCreateTicketManagerStatusTransitionMutation,
  useUpdateTicketManagerStatusTransitionMutation,
  useDeleteTicketManagerStatusTransitionMutation,
  useResetTicketManagerStatusTransitionsMutation,
} = TicketStatusEndpoints;
